import { MultilanguageContent } from './message';

export type ChatChoiceSelectionMode = 'single' | 'multi';

/** API stores MultilanguageContent; socket resolves to a plain string for the customer locale. */
export type ChatChoiceLocalizedText = MultilanguageContent | string;

export interface ChatChoiceOption {
  id: number;
  chatChoiceId: number;
  label: ChatChoiceLocalizedText;
  value: string;
  order: number;
  createdDate?: string;
  updatedDate?: string;
}

export interface ChatChoice {
  id: number;
  title: ChatChoiceLocalizedText;
  selectionMode: ChatChoiceSelectionMode;
  order: number;
  options: ChatChoiceOption[];
  createdDate?: string;
  updatedDate?: string;
}

export interface ChatChoiceOptionInput {
  label: MultilanguageContent;
  value: string;
  order?: number;
}

export interface ChatChoiceCreateRequest {
  title: MultilanguageContent;
  selectionMode: ChatChoiceSelectionMode;
  order?: number;
  options?: ChatChoiceOptionInput[];
}

export interface ChatChoiceUpdateRequest {
  title?: MultilanguageContent;
  selectionMode?: ChatChoiceSelectionMode;
  order?: number;
  options?: ChatChoiceOptionInput[];
}

export interface ChatPromptChoiceLink {
  choiceId: number;
  order: number;
}

export interface ChatChoiceListResponse {
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  data: ChatChoice[];
}

export const CHOICE_OPTION_VALUE_PATTERN = /^[a-zA-Z0-9_-]+$/;

export const CHOICE_DEFAULT_LANG_CODE = 'en-EN';

export interface ChatChoiceSelectedOption {
  optionId?: number;
  label: string;
  value: string;
}

export interface ChatChoiceAnswer {
  choiceId: number;
  selected: ChatChoiceSelectedOption[];
}

/** Resolve title/label for display (socket strings or admin MultilanguageContent). */
export const resolveChoiceLocalizedText = (
  value: ChatChoiceLocalizedText | null | undefined,
  preferredLang: string = CHOICE_DEFAULT_LANG_CODE
): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }
  const preferred = value.find(item => item.lang_code === preferredLang)?.content?.trim();
  if (preferred) {
    return preferred;
  }
  const english = value.find(item => item.lang_code === CHOICE_DEFAULT_LANG_CODE)?.content?.trim();
  if (english) {
    return english;
  }
  return value[0]?.content?.trim() ?? '';
};

export const toChoiceMultilanguageContent = (
  value: ChatChoiceLocalizedText | null | undefined
): MultilanguageContent => {
  if (value == null) {
    return [];
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [{ lang_code: CHOICE_DEFAULT_LANG_CODE, content: trimmed }] : [];
  }
  return value
    .filter(item => item.lang_code?.trim() && item.content?.trim())
    .map(item => ({
      lang_code: item.lang_code.trim(),
      content: item.content.trim()
    }));
};
